/**
 * ChangeSiteModal — #60 / DEC-365: change a job's pickup site, from the job detail or In Review.
 *
 * Lists, from GET /v1/jobs/:id/site-options:
 *   1. "Other wells on this location" — for a location job, which linked well the load came
 *      from (sets sourceWellId; the site itself stays);
 *   2. "Other sites for this customer" — the customer's other locations and standalone wells
 *      (same customer only, D2), searchable; a location may also name its well.
 * A reason code is required, and words for "Other" (10+ characters, D3). The change is sent
 * with the site the dispatcher was looking at (expectedSite), so a change made meanwhile by the
 * driver or another dispatcher is refused instead of overwritten.
 *
 * Gauging data is always kept (D10): tanks with the same name at the new site are re-linked by
 * the server, the rest are left unlinked and picked in UnlinkedTanksPanel.
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import {
  JOB_SITE_CHANGE_REASON_CODES,
  JOB_SITE_CHANGE_REASON_LABELS,
  JOB_SITE_CHANGE_REASON_OTHER_MIN,
  type JobSiteChangeReasonCode,
  type JobSiteChangeResponse,
  type SiteOption,
  type SiteRef,
} from '@lodex-pro/shared/jobs/site-change';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { invalidateAfterSiteChange, readSiteChangeError, useSiteOptions } from './site-change-api';

export interface ChangeSiteTarget {
  readonly jobId: string;
  readonly haulId: string | null;
  /** What the dispatcher sees now: "BATTERY ONE · WELL 2". */
  readonly currentLabel: string | null;
  /** Whether a run ticket exists (the tank note only matters then). */
  readonly hasCapture: boolean;
}

export interface ChangeSiteModalProps {
  readonly target: ChangeSiteTarget | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onChanged: (result: JobSiteChangeResponse) => void;
}

/** A pick, encoded for the radio group: "LOCATION|<id>|<wellId or ''>" or "WELL|<id>|". */
export function siteKey(ref: SiteRef): string {
  return `${ref.kind}|${ref.id}|${ref.sourceWellId ?? ''}`;
}

export function refFromKey(key: string): SiteRef | null {
  const [kind, id, well] = key.split('|');
  if (id === undefined || id === '') return null;
  if (kind === 'LOCATION')
    return { kind, id, sourceWellId: well === '' || well === undefined ? null : well };
  if (kind === 'WELL') return { kind, id, sourceWellId: null };
  return null;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return (): void => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
  display: 'block',
  marginBottom: '4px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

const listStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxHeight: '180px',
  overflowY: 'auto',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '4px',
  padding: '6px 8px',
};

const optionStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  color: 'var(--color-text-primary)',
};

function siteName(s: SiteOption): string {
  return s.name ?? (s.kind === 'WELL' ? (s.apiNumber ?? 'Unnamed well') : 'Unnamed site');
}

export function ChangeSiteModal({
  target,
  open,
  onClose,
  onChanged,
}: ChangeSiteModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState('');
  const q = useDebounced(search, 250);
  const [picked, setPicked] = React.useState<string | null>(null);
  const [reasonCode, setReasonCode] = React.useState<JobSiteChangeReasonCode | ''>('');
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setSearch('');
      setPicked(null);
      setReasonCode('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const jobId = target?.jobId ?? null;
  const options = useSiteOptions(apiOpts, jobId, q, open);
  const current = options.data?.current ?? null;
  const currentKey = current === null ? null : siteKey(current);
  const sites = options.data?.sites ?? [];
  const currentLocation =
    current?.kind === 'LOCATION'
      ? sites.find((s) => s.kind === 'LOCATION' && s.id === current.id)
      : undefined;
  const otherSites = sites.filter((s) => current === null || s.id !== current.id);
  const pickedRef = picked === null ? null : refFromKey(picked);
  const pickedLocation =
    pickedRef?.kind === 'LOCATION' && pickedRef.id !== current?.id
      ? sites.find((s) => s.kind === 'LOCATION' && s.id === pickedRef.id)
      : undefined;

  const mutation = useMutation<JobSiteChangeResponse, Error, { jobId: string; body: string }>({
    mutationFn: async ({ jobId: id, body }) => {
      const res = await authedFetch(apiOpts, `/v1/jobs/${id}/site`, { method: 'POST', body });
      if (!res.ok) throw new Error(await readSiteChangeError(res));
      return res.json() as Promise<JobSiteChangeResponse>;
    },
    onSuccess: (data, vars) => {
      invalidateAfterSiteChange(queryClient, vars.jobId);
      if (!data.changed) {
        toast.success('The job is already at that site.');
      } else if (data.unlinkedTankNames.length > 0) {
        toast.warning(
          `Site changed to ${data.job.siteName ?? 'the new site'}. Pick the tank for: ${data.unlinkedTankNames.join(', ')}.`,
        );
      } else {
        toast.success(`Site changed to ${data.job.siteName ?? 'the new site'}.`);
      }
      onChanged(data);
    },
    onError: (err) => setError(err.message),
  });

  const needsWords = reasonCode === 'OTHER';
  const reasonOk =
    reasonCode !== '' && (!needsWords || reason.trim().length >= JOB_SITE_CHANGE_REASON_OTHER_MIN);
  const canSubmit =
    pickedRef !== null &&
    picked !== currentKey &&
    reasonOk &&
    !mutation.isPending &&
    jobId !== null;

  const submit = (): void => {
    if (!canSubmit || pickedRef === null || jobId === null) return;
    setError(null);
    mutation.mutate({
      jobId,
      body: JSON.stringify({
        toSite: pickedRef,
        ...(current !== null ? { expectedSite: current } : {}),
        reasonCode,
        reason: reason.trim() === '' ? null : reason.trim(),
      }),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg">
        <DialogHeader>
          <DialogTitle>{`Change site — ${target?.haulId ?? 'job'}`}</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <p style={{ fontSize: '14px', margin: 0, color: 'var(--color-text-secondary)' }}>
            Now: <strong>{target?.currentLabel ?? 'No site'}</strong>. Only this customer&apos;s
            sites can be picked; the Haul ID and billing customer stay the same.
          </p>

          {options.isError && (
            <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
              {options.error.message}
            </p>
          )}

          {currentLocation?.kind === 'LOCATION' && currentLocation.wells.length > 0 && (
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend style={labelStyle}>Other wells on this location</legend>
              <div style={listStyle} data-testid="change-site-wells">
                {currentLocation.wells.map((w) => {
                  const key = siteKey({
                    kind: 'LOCATION',
                    id: currentLocation.id,
                    sourceWellId: w.wellId,
                  });
                  return (
                    <label key={w.wellId} style={optionStyle}>
                      <input
                        type="radio"
                        name="change-site"
                        checked={(picked ?? currentKey) === key}
                        onChange={(): void => setPicked(key)}
                      />
                      {w.wellName ?? w.apiNumber ?? 'Unnamed well'}
                      {key === currentKey && (
                        <span style={{ color: 'var(--color-text-tertiary)' }}>(current)</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}

          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={labelStyle}>Other sites for this customer</legend>
            <input
              type="search"
              value={search}
              onChange={(e): void => setSearch(e.target.value)}
              placeholder="Search by site, well name or API number"
              aria-label="Search sites"
              style={{ ...inputStyle, marginBottom: '6px' }}
            />
            <div style={listStyle} data-testid="change-site-sites">
              {options.isLoading && (
                <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                  Loading sites…
                </span>
              )}
              {!options.isLoading && otherSites.length === 0 && (
                <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                  No other sites for this customer{q.trim() === '' ? '' : ' match'}.
                </span>
              )}
              {otherSites.map((s) => {
                const key = siteKey(
                  s.kind === 'LOCATION'
                    ? { kind: 'LOCATION', id: s.id, sourceWellId: null }
                    : { kind: 'WELL', id: s.id, sourceWellId: null },
                );
                const isPicked =
                  pickedRef !== null && pickedRef.kind === s.kind && pickedRef.id === s.id;
                return (
                  <label key={key} style={optionStyle}>
                    <input
                      type="radio"
                      name="change-site"
                      checked={isPicked}
                      onChange={(): void => setPicked(key)}
                    />
                    {siteName(s)}
                    <span style={{ color: 'var(--color-text-tertiary)', fontSize: '12px' }}>
                      {s.kind === 'WELL' ? 'Well' : 'Location'}
                    </span>
                  </label>
                );
              })}
              {options.data?.truncated === true && (
                <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                  More sites match — type to narrow the list.
                </span>
              )}
            </div>
          </fieldset>

          {pickedLocation?.kind === 'LOCATION' && pickedLocation.wells.length > 0 && (
            <div>
              <label htmlFor="change-site-well" style={labelStyle}>
                Which well on {siteName(pickedLocation)}? (optional)
              </label>
              <select
                id="change-site-well"
                value={pickedRef?.sourceWellId ?? ''}
                onChange={(e): void =>
                  setPicked(
                    siteKey({
                      kind: 'LOCATION',
                      id: pickedLocation.id,
                      sourceWellId: e.target.value === '' ? null : e.target.value,
                    }),
                  )
                }
                style={inputStyle}
              >
                <option value="">Not recorded</option>
                {pickedLocation.wells.map((w) => (
                  <option key={w.wellId} value={w.wellId}>
                    {w.wellName ?? w.apiNumber ?? 'Unnamed well'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="change-site-reason-code" style={labelStyle}>
              Why
            </label>
            <select
              id="change-site-reason-code"
              value={reasonCode}
              onChange={(e): void => setReasonCode(e.target.value as JobSiteChangeReasonCode | '')}
              style={inputStyle}
            >
              <option value="">Pick a reason…</option>
              {JOB_SITE_CHANGE_REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {JOB_SITE_CHANGE_REASON_LABELS[code]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="change-site-reason" style={labelStyle}>
              {needsWords ? 'Say why' : 'Note (optional)'}
            </label>
            <textarea
              id="change-site-reason"
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              maxLength={500}
              placeholder={
                needsWords
                  ? `At least ${String(JOB_SITE_CHANGE_REASON_OTHER_MIN)} characters`
                  : undefined
              }
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          {target?.hasCapture === true && (
            <p style={{ fontSize: '13px', margin: 0, color: 'var(--color-text-secondary)' }}>
              The run ticket&apos;s readings are kept. Tanks with the same name at the new site are
              linked to it; any others are left for you to pick afterwards.
            </p>
          )}
        </div>

        {error !== null && (
          <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Keep site
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={submit}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
              opacity: canSubmit ? 1 : 0.6,
            }}
          >
            {mutation.isPending ? 'Changing…' : 'Change site'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
